<template>
  <div v-if="showBanner" class="cookie-consent-banner fixed bottom-0 left-0 right-0 z-50 print:hidden">
    <Card class="rounded-none border-t border-gray-200 shadow-lg">
      <template #content>
        <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <!-- Content -->
          <div class="flex-1">
            <h3 class="text-lg font-semibold text-gray-900 mb-2">
              {{ $t('cookieConsent.title') }}
            </h3>
            <p class="text-sm text-gray-600 mb-4">
              {{ $t('cookieConsent.description') }}
            </p>

            <!-- Detailed Options (when expanded) -->
            <div v-if="showDetails" class="space-y-4 mb-4">
              <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <!-- Essential Cookies -->
                <Card class="bg-gray-50">
                  <template #content>
                    <div class="flex items-center justify-between mb-2">
                      <h4 class="font-medium text-gray-900">
                        {{ $t('cookieConsent.essential.title') }}
                      </h4>
                      <Tag :value="$t('cookieConsent.essential.required')" severity="success" />
                    </div>
                    <p class="text-xs text-gray-600 mb-2">
                      {{ $t('cookieConsent.essential.description') }}
                    </p>
                    <ToggleSwitch v-model="consent.essential" disabled />
                  </template>
                </Card>

                <!-- Functional Cookies -->
                <Card class="bg-gray-50">
                  <template #content>
                    <div class="flex items-center justify-between mb-2">
                      <h4 class="font-medium text-gray-900">
                        {{ $t('cookieConsent.functional.title') }}
                      </h4>
                      <Tag :value="$t('cookieConsent.functional.optional')" severity="info" />
                    </div>
                    <p class="text-xs text-gray-600 mb-2">
                      {{ $t('cookieConsent.functional.description') }}
                    </p>
                    <ToggleSwitch v-model="consent.functional" />
                  </template>
                </Card>

                <!-- Analytics Cookies -->
                <Card class="bg-gray-50">
                  <template #content>
                    <div class="flex items-center justify-between mb-2">
                      <h4 class="font-medium text-gray-900">
                        {{ $t('cookieConsent.analytics.title') }}
                      </h4>
                      <Tag :value="$t('cookieConsent.analytics.optional')" severity="help" />
                    </div>
                    <p class="text-xs text-gray-600 mb-2">
                      {{ $t('cookieConsent.analytics.description') }}
                    </p>
                    <ToggleSwitch v-model="consent.analytics" />
                  </template>
                </Card>

                <!-- Marketing Cookies -->
                <Card class="bg-gray-50">
                  <template #content>
                    <div class="flex items-center justify-between mb-2">
                      <h4 class="font-medium text-gray-900">
                        {{ $t('cookieConsent.marketing.title') }}
                      </h4>
                      <Tag :value="$t('cookieConsent.marketing.optional')" severity="warning" />
                    </div>
                    <p class="text-xs text-gray-600 mb-2">
                      {{ $t('cookieConsent.marketing.description') }}
                    </p>
                    <ToggleSwitch v-model="consent.marketing" />
                  </template>
                </Card>
              </div>
            </div>

            <!-- Links -->
            <div class="flex flex-wrap gap-4 text-sm">
              <Button
                @click="$emit('showPrivacyPolicy')"
                :label="$t('cookieConsent.links.privacyPolicy')"
                link
                class="text-blue-600 hover:text-blue-800 p-0"
              />
              <Button
                @click="$emit('showCookiePolicy')"
                :label="$t('cookieConsent.links.cookiePolicy')"
                link
                class="text-blue-600 hover:text-blue-800 p-0"
              />
              <Button
                @click="toggleDetails"
                :label="showDetails ? $t('cookieConsent.links.hideDetails') : $t('cookieConsent.links.showDetails')"
                link
                class="text-gray-600 hover:text-gray-800 p-0"
              />
            </div>
          </div>

          <!-- Actions -->
          <div class="flex flex-col sm:flex-row gap-3 lg:flex-col lg:min-w-[200px]">
            <Button
              @click="acceptSelected"
              :label="$t('cookieConsent.actions.acceptSelected')"
              severity="success"
              class="w-full sm:w-auto lg:w-full"
            />
            <Button
              @click="acceptAll"
              :label="$t('cookieConsent.actions.acceptAll')"
              severity="info"
              class="w-full sm:w-auto lg:w-full"
            />
            <Button
              @click="acceptEssentialOnly"
              :label="$t('cookieConsent.actions.essentialOnly')"
              severity="secondary"
              outlined
              class="w-full sm:w-auto lg:w-full"
            />
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import Button from 'primevue/button'
import ToggleSwitch from 'primevue/toggleswitch'
import Card from 'primevue/card'
import Tag from 'primevue/tag'

interface CookieConsent {
  essential: boolean
  functional: boolean
  analytics: boolean
  marketing: boolean
}

const emit = defineEmits<{
  showPrivacyPolicy: []
  showCookiePolicy: []
  consentUpdated: [consent: CookieConsent]
}>()

const showBanner = ref(false)
const showDetails = ref(false)

const consent = reactive<CookieConsent>({
  essential: true, // Always true, cannot be disabled
  functional: false,
  analytics: false,
  marketing: false,
})

const CONSENT_STORAGE_KEY = 'cookie-consent-preferences'
const CONSENT_VERSION = '1.0'

onMounted(() => {
  checkExistingConsent()
})

const checkExistingConsent = () => {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      // Check if consent version matches (for future updates)
      if (parsed.version === CONSENT_VERSION) {
        // User has already given consent, don't show banner
        Object.assign(consent, parsed.consent)
        showBanner.value = false
        return
      }
    }
    // No valid consent found, show banner
    showBanner.value = true
  } catch (error) {
    console.error('Error checking existing consent:', error)
    showBanner.value = true
  }
}

const saveConsent = (consentData: CookieConsent) => {
  try {
    const consentRecord = {
      version: CONSENT_VERSION,
      consent: consentData,
      timestamp: new Date().toISOString(),
    }
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentRecord))
    emit('consentUpdated', consentData)
    showBanner.value = false
  } catch (error) {
    console.error('Error saving consent:', error)
  }
}

const acceptSelected = () => {
  saveConsent({ ...consent })
}

const acceptAll = () => {
  const allConsent: CookieConsent = {
    essential: true,
    functional: true,
    analytics: true,
    marketing: true,
  }
  Object.assign(consent, allConsent)
  saveConsent(allConsent)
}

const acceptEssentialOnly = () => {
  const essentialOnly: CookieConsent = {
    essential: true,
    functional: false,
    analytics: false,
    marketing: false,
  }
  Object.assign(consent, essentialOnly)
  saveConsent(essentialOnly)
}

const toggleDetails = () => {
  showDetails.value = !showDetails.value
}

// Public method to show banner (for settings)
const showConsentBanner = () => {
  showBanner.value = true
}

// Public method to get current consent
const getCurrentConsent = (): CookieConsent => {
  return { ...consent }
}

// Expose methods for parent components
defineExpose({
  showConsentBanner,
  getCurrentConsent,
})
</script>

<style scoped>
/* Custom styles for better mobile experience */
@media (max-width: 640px) {
  .fixed {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
  }
}
</style>
